import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Hook to prefix paths with the current language.
 * Usage: const { localePath } = useLocalizedPath();
 *        <Link to={localePath("/services")} />
 */
export function useLocalizedPath() {
  const { language } = useLanguage();

  // Per-language segment overrides (e.g. /avis in FR, /reviews in EN)
  const SEGMENT_MAP: Record<string, Record<string, string>> = {
    en: { "/avis": "/reviews" },
    fr: { "/reviews": "/avis" },
  };

  const localizeSegments = (p: string) => {
    const map = SEGMENT_MAP[language] || {};
    for (const [from, to] of Object.entries(map)) {
      if (p === from) return to;
      if (p.startsWith(from + "/")) return to + p.slice(from.length);
    }
    return p;
  };

  const localePath = (path: string) => {
    // Handle root path
    if (path === "/") return `/${language}`;
    // Already has lang prefix? Return as-is
    if (path.startsWith(`/${language}/`) || path === `/${language}`) return path;
    const normalized = path.startsWith("/") ? path : `/${path}`;
    const mapped = localizeSegments(normalized);
    return `/${language}${mapped}`;
  };

  return { localePath, language };
}
